// University of Oklahoma: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - OU's Common Data Set 2025-2026 (Norman campus only), published by
//    Institutional Research and Reporting one PDF per section on ou.edu:
//    "IRR CDS-C.pdf" for C1 applicants, admits and enrollees for Fall 2025
//    with the residency table, C8 test policy for Fall 2027 and C21/C22
//    plans. The SOURCES url is the section C file itself.
//  - OU Admissions: Freshman Admissions (dates and materials), "10 Reasons to
//    Apply by November 1" (read, not cited: it names fall 2027 as the term
//    for the November 1 early action deadline), Test Scores in Admissions Decisions, and the
//    Weitzenhoffer Family College of Fine Arts majors page (auditions).
//    Quirk: the Freshman Admissions dates carry no year; they are the dates
//    as posted while the application for fall 2027 is open, and the early
//    action page ties November 1 to fall 2027. OU publishes no decision
//    dates; it says a decision comes by mail within four weeks of a
//    complete file, if not sooner.
//  - OU Student Financial Center, Cost of Attendance, which says its figures
//    are for the 2026-2027 academic year (undergraduate, living on campus).
//    Departure: this is used instead of the CDS G1/G5, because the CDS
//    section G names both 2025-2026 and 2026-2027 as its year and lists
//    required fees of $4,953, while the 2026-2027 cost page lists fees of
//    $9,971 (tuition, housing and food, books, transportation and personal
//    costs match). OU's own totals ($40,145 in-state, $58,823 out-of-state)
//    add $68 in loan fees; the rows here leave loan fees out, so the totals
//    are $68 lower.
//  - College Scorecard, school id 207500 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'OU Common Data Set 2025-2026, section C',
        url: 'https://www.ou.edu/content/dam/irr/docs/common-data-sets/norman-campus-only/2025-26-nc/IRR%20CDS-C.pdf',
        read: '2026-10-01',
    },
    freshman: {
        label: 'OU Admissions, Freshman Admissions',
        url: 'https://www.ou.edu/admissions/apply/freshman',
        read: '2026-10-01',
    },
    tests: {
        label: 'OU Admissions, Test Scores in Admissions Decisions',
        url: 'https://www.ou.edu/admissions/apply/freshman/requirements-factors/test-scores',
        read: '2026-10-01',
    },
    fineArts: {
        label: 'OU Admissions, Weitzenhoffer Family College of Fine Arts',
        url: 'https://www.ou.edu/admissions/academics/fine-arts',
        read: '2026-10-01',
    },
    costs: {
        label: 'OU Student Financial Center, Cost of Attendance',
        url: 'https://www.ou.edu/sfc/estimating-costs/cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?207500-University-of-Oklahoma-Norman-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-oklahoma',
    kbId: 'university_of_oklahoma',
    name: 'University of Oklahoma',
    shortName: 'OU',
    aliases: ['OU'],
    location: 'Norman, OK',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (OU brand design system, colors); decoration only.
    branding: {
        primary: '#841617',
        secondary: '#DDCBA4',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_oklahoma_logo.png',
    },

    // The direct answer and short FAQ (#4090, founder 2026-10-13: content
    // only, no title change before the Oct 27 title-test read). Every figure
    // restates one the page already shows from the source named beside it;
    // CollegePages.test.jsx fails a number the rest of the page does not hold.
    // The search it answers: ou acceptance rate.
    faq: {
        answer: { text: 'The University of Oklahoma (OU) admitted 77.6% of first-year applicants for fall 2025, 22,660 of the 29,195 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the OU acceptance rate?', a: 'For fall 2025 entry, OU admitted 22,660 of 29,195 first-year applicants, an acceptance rate of 77.6%. Oklahoma residents were admitted at 75.9% and out-of-state applicants at 80.3%.', source: 'cds', where: 'section C1' },
            { q: 'What are OU\'s deadlines for fall 2027?', a: 'Early action is November 1, the final scholarship admission deadline is December 15, and the final admission deadline is February 1. OU says you should hear within four weeks of a complete file.', source: 'freshman' },
            { q: 'Is OU early action binding?', a: 'No. Early action at OU is not binding, and it is not restrictive: you may apply early to other colleges too.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'Does OU require the SAT or ACT?', a: 'No. OU is test-optional. On the application you choose whether your ACT, SAT or CLT scores are used, and OU superscores the ACT and the SAT.', source: 'tests' },
            { q: 'What does a year at OU cost?', a: 'For 2026-2027, a full-time undergraduate living on campus comes to $40,077 in-state and $58,755 out-of-state, before average loan fees.', source: 'costs' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 29195,
        admitted: 22660,
        enrolled: 6229,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 6694, admitted: 5083 },
            { group: 'Out-of-state', applied: 20948, admitted: 16828 },
            { group: 'International', applied: 1553, admitted: 749 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'OU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'OU has no early decision plan. Its early action plan closes November 1, is not binding and does not stop you applying early elsewhere; decisions are released on a rolling basis. The Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to OU, not to a college. The performing arts schools ask for more:',
        rows: [
            { school: 'Dance, Drama, Music and Musical Theatre', requirement: 'Admission to OU plus a separate application to the school and an audition. The visual arts majors in the same college do not list an audition.' },
        ],
        source: 'fineArts',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Final admission deadline', value: 'February 1' },
        rows: [
            { plan: 'Early action', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Final scholarship admission deadline', deadline: 'December 15', decision: null, binding: false },
            { plan: 'Final admission deadline', deadline: 'February 1', decision: null, binding: false },
        ],
        note: 'The application opens August 1. OU does not publish decision dates; it says you should hear by mail within four weeks of a complete file, if not sooner. You can update ACT, SAT or CLT scores through April 30, and the enrollment deposit is due May 1.',
        source: 'freshman',
        binding: 'Early action at OU is not binding, and it is not restrictive: you may apply early to other colleges too.',
        bindingSource: 'cds',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'OU is test-optional. On the application you choose whether your ACT, SAT or CLT scores are used; if you choose to use them, they are considered in the review, and OU superscores the ACT and the SAT. You may self-report scores, verified before you enroll. Without a score, you take placement exams before enrolling.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by April 30.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [5532, 24210] },
            { item: 'Required fees', amounts: [9971, 9971] },
            { item: 'Food and housing (on campus)', amounts: [16381, 16381] },
            { item: 'Books and supplies', amounts: [800, 800] },
            { item: 'Transportation', amounts: [1728, 1728] },
            { item: 'Personal expenses', amounts: [5665, 5665] },
        ],
        totals: [40077, 58755],
        note: 'For a full-time undergraduate living on campus, fall and spring. OU\'s own totals are $68 higher because they add average loan fees.',
        source: 'costs',
        netPrice: {
            average: 15531,
            byIncome: [
                { income: '$0 to $30,000', amount: 11203 },
                { income: '$30,001 to $48,000', amount: 10500 },
                { income: '$48,001 to $75,000', amount: 13101 },
                { income: '$75,001 to $110,000', amount: 19131 },
                { income: 'Over $110,000', amount: 19219 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from OU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
